import { useEffect, useState, useRef } from 'react';
import { vars } from '@etg/wings';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { getFallbackImageId } from './utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const spinnerStyles = (bgColor, size) => css`
  background-color: ${bgColor};
  border-radius: 50%;
  height: ${size}px;
  margin: 0 auto;
  position: relative;
  width: ${size}px;
`;
const fillStyles = (duration, size) => css`
  animation: spin ${duration} infinite linear;
  background: linear-gradient(
    to top,
    rgb(255 255 255 / 0%) 5%,
    ${vars.colors.decoration.dark} 100%
  );
  border-radius: 50%;
  clip: rect(0 ${size / 2}px ${size}px 0);
  height: 100%;
  position: absolute;
  width: 100%;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
const imageStyles = (bgColor, img) => css`
  background-color: ${bgColor};
  background-image: ${img ? `url(${img})` : `none`};
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 50%;
  height: 100%;
  opacity: ${img ? '1' : '0'};
  transition: opacity 0.25s ease-in-out;
  width: 100%;
`;
const imageWrapperStyles = backgroundColor => css`
  background-color: ${backgroundColor};
  border-radius: 50%;
  height: calc(100% - 34px);
  margin-left: 17px;
  margin-top: 17px;
  position: absolute;
  width: calc(100% - 34px);

  @media ${mediaQueries.medium.up} {
    height: calc(100% - 40px);
    margin-left: 20px;
    margin-top: 20px;
    width: calc(100% - 40px);
  }

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 17px;

    @media ${mediaQueries.medium.up} {
      margin-right: 20px;
    }
  }
`;
const fetchFallbackSpinnerImage = async () => {
  const fallbackImageId = getFallbackImageId();
  const fallback = await import(`@eti/assets/images/progressSpinner/fallback-0${fallbackImageId}@2x.jpg`);
  return fallback.default;
};
const fetchTransitionSpinnerImage = async iataCode => {
  if (iataCode) {
    try {
      const destination = await import(`@eti/assets/images/progressSpinner/${iataCode}@2x.jpg`);
      return destination.default;
    } catch {
      return fetchFallbackSpinnerImage();
    }
  }
  return fetchFallbackSpinnerImage();
};
const ProgressSpinner = _ref => {
  let {
    bgColor = etiColors.grayScale[5],
    destinationIataCode,
    duration = '10s',
    img,
    imgBgColor = etiColors.white,
    size = 300
  } = _ref;
  const [spinnerImage, setSpinnerImage] = useState(null);
  const isUnmounted = useRef(false);
  useEffect(() => {
    const loadSpinnerImage = async () => {
      if (!img) {
        const productImage = await fetchTransitionSpinnerImage(destinationIataCode);
        if (!isUnmounted.current) {
          setSpinnerImage(productImage);
        }
      }
    };
    loadSpinnerImage();
    return () => {
      isUnmounted.current = true;
    };
  }, [destinationIataCode, img]);
  return /*#__PURE__*/_jsxs("div", {
    className: cx('progressSpinner', spinnerStyles(bgColor, size)),
    children: [/*#__PURE__*/_jsx("div", {
      className: cx('progressSpinner__fill', fillStyles(duration, size))
    }), /*#__PURE__*/_jsx("div", {
      className: imageWrapperStyles(imgBgColor),
      children: /*#__PURE__*/_jsx("div", {
        className: cx('progressSpinner__image', imageStyles(bgColor, img || spinnerImage)),
        "data-testid": "progressSpinner-image"
      })
    })]
  });
};
export default ProgressSpinner;